import { useLocation } from "react-router-dom";

import { Info } from "@assets";
import { Button } from "@components/Button";
import { ErrorComponent } from "@components/ErrorComponent";
import { Row } from "@components/Flex";
import { SelectionInput, TextInput, TextInputType } from "@components/Input";
import { Loader } from "@components/Loader";
import { MainLayout, MainLayoutProps } from "@components/MainLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Body } from "@components/Typography";
import { IRA_DISTRIBUTION_FORM_URL } from "@constants";
import { AccountType } from "@ctypes";
import {
  useAvailableToWithdraw,
  useCreateWithdrawal,
  useErrorBanner,
  useGoals,
  useIsWithdrawalAllowed,
  useModal,
  useTheme,
} from "@hooks";
import { i18n, Translations } from "@i18n";
import {
  convertCentsToDollar,
  convertDollarToCents,
  formatCurrency,
  formatCurrencyInput,
  isValidDecimalInput,
} from "@utils";
import { useState } from "react";
import { AccountNumberSection } from "./AccountNumberSection";
import { AvailableFundsModal } from "./AvailableFundsModal";
import { CancelWithdrawalModal } from "./CancelWithdrawalModal";
import { content } from "./content";
import { useStyles } from "./styles";
import { WithdrawalRouteProps } from "./types";
import { WithdrawalStatusBanner } from "./WithdrawalStatusBanner";
import { WithdrawalSuccessModal } from "./WithdrawalSuccessModal";

const MINIMUM_AMOUNT = 1;

export const WithdrawRoute = () => {
  const styles = useStyles();
  const { theme } = useTheme();

  const location = useLocation();
  const { showErrorBanner } = useErrorBanner();
  const { show } = useModal();

  const { goalId } = (location.state as WithdrawalRouteProps);

  const { goals } = useGoals();
  const [createWithdrawal, { loading: isCreatingWithdrawal }] = useCreateWithdrawal();

  const goal = goals.find((g) => g.id === goalId);

  const accounts = goal?.accounts ?? [];
  const options = accounts.map((a) => a?.title ?? "");

  const [selectedIndex, setSelectedIndex] = useState(0);

  const selectedAccount = accounts[selectedIndex];

  const accountType = selectedAccount?.type ?? AccountType.GeneralInvesting;
  const subaccountId = selectedAccount?.subaccounts?.[0]?.id;

  const {
    data,
    loading: availableToWithdrawLoading,
    error: availableToWithdrawError,
    refetch: refetchAvailableToWithdraw,
  } = useAvailableToWithdraw({
    goalId,
    subaccountId,
  });

  const {
    data: isWithdrawalAllowedData,
    loading: isWithdrawalAllowedLoading,
    error: isWithdrawalAllowedError,
    refetch: refetchIsWithdrawalAllowed,
  } = useIsWithdrawalAllowed({ goalId, subaccountId });

  const loading = availableToWithdrawLoading || isWithdrawalAllowedLoading;
  const error = availableToWithdrawError || isWithdrawalAllowedError;

  const refetch = () => {
    return Promise.allSettled([refetchAvailableToWithdraw(), refetchIsWithdrawalAllowed()]);
  };

  const isWithdrawalAllowed = isWithdrawalAllowedData?.allowed;
  const isCancellationRequired = isWithdrawalAllowedData?.cancellationRequired;
  const withdrawalReason = isWithdrawalAllowedData?.reason;

  const maxAmount = convertCentsToDollar(data?.netAvailableToWithdrawInCents);

  const [amountInput, setAmountInput] = useState("");
  const amount = Number(amountInput.replace(/[^0-9.]/g, ""));

  const goalName = goal?.name ?? "";

  const availableFunds = formatCurrency(
    convertCentsToDollar(data?.netAvailableToWithdrawInCents),
    true,
  );

  const pendingContributions = formatCurrency(
    convertCentsToDollar(data?.pendingContributionsInCents),
    true,
  );

  const pendingWithdrawals = formatCurrency(
    convertCentsToDollar(data?.pendingWithdrawalsInCents),
    true,
  );

  const lockedPromoBonus = formatCurrency(
    convertCentsToDollar(data?.lockedPromotionalDollarsInCents),
    true,
  );

  const isBelowMinAmount = amount < MINIMUM_AMOUNT && amountInput.length > 0;
  const isAboveMaxAmount = amount > maxAmount;
  const isInvalidInput = !isValidDecimalInput(amountInput);

  const inputError = (() => {
    if (isBelowMinAmount) {
      return i18n.t(Translations.WithdrawalRouteAmountInputMinAmountError, {
        amount: formatCurrencyInput({
          input: MINIMUM_AMOUNT.toFixed(2),
          decimals: true,
        }),
      });
    }

    if (isAboveMaxAmount) {
      return i18n.t(Translations.WithdrawalRouteAmountInputMaxAmountError, {
        amount: formatCurrencyInput({
          input: maxAmount.toFixed(2),
          decimals: true,
        }),
      });
    }

    if (isInvalidInput) return i18n.t(Translations.WithdrawalRouteAmountInputInvalidFormatError);

    return undefined;
  })();

  const isIRAAccount = [
    AccountType.TraditionalIra,
    AccountType.IraTraditional,
    AccountType.IraRoth,
    AccountType.RothIra,
  ].includes(accountType);

  const disabled =
    !isWithdrawalAllowed ||
    (isIRAAccount ? false : Boolean(inputError || amountInput.length === 0));

  const primaryCTALabel = isIRAAccount
    ? i18n.t(Translations.WithdrawRouteViewFormCTA)
    : i18n.t(Translations.WithdrawRoutePrimaryCTA);

  const props: MainLayoutProps = {
    pageHeader: i18n.t(Translations.WithdrawRouteHeader),
  };

  const onChangeAmount = (input: string) => {
    const newAmount = formatCurrencyInput({
      input,
      decimals: true,
    });
    setAmountInput(newAmount);
  };

  const showInfoModal = () => {
    show(<AvailableFundsModal />);
  };

  const handleCreateWithdrawal = async (cancelExistingWithdrawal = false) => {
    if (!goal?.id || !amount) return showErrorBanner();

    const amountInCents = convertDollarToCents(amount);

    try {
      await createWithdrawal({
        variables: {
          amountInCents,
          goalId,
          subaccountId,
          cancelExistingWithdrawal,
        },
      });

      refetch();

      show(<WithdrawalSuccessModal />, false);
    } catch (e: any) {
      const message = e.graphQLErrors?.[0]?.message;
      setTimeout(() => {
        showErrorBanner(message);
      }, 0);
    }
  };

  const handleSubmit = async () => {
    if (isIRAAccount) {
      return window.open(IRA_DISTRIBUTION_FORM_URL, "_blank");
    }

    if (!goal?.id || !amount) return showErrorBanner();

    if (isCancellationRequired) {
      show(<CancelWithdrawalModal onConfirm={() => handleCreateWithdrawal(true)} />);
      return;
    }

    await handleCreateWithdrawal(false);
  };

  if (loading) return <Loader />;
  if (error) return <ErrorComponent />;

  return (
    <MainLayout {...props}>
      <Spacing size={SpacingSizes.M} />

      {withdrawalReason && <WithdrawalStatusBanner reasonCode={withdrawalReason} />}

      {!isIRAAccount && (
        <>
          <TextInput
            label={i18n.t(Translations.WithdrawRouteAmountLabel)}
            inputType={TextInputType.Secondary}
            value={amountInput}
            onChangeText={onChangeAmount}
            placeholder={i18n.t(Translations.WithdrawRouteAmountPlaceholder)}
            error={inputError}
            disabled={!isWithdrawalAllowed}
          />
          <Spacing size={SpacingSizes.XS} />
        </>
      )}

      <div style={styles.container}>
        <Row>
          <Row style={styles.left}>
            <Body style={styles.text}>{i18n.t(Translations.WithdrawRouteAvailableFunds)}</Body>
            <div onClick={showInfoModal}>
              <Info size={18} style={styles.icon} fill={theme.withdrawal.availableFunds.icon} />
            </div>
          </Row>
          <Body style={styles.text}>{availableFunds}</Body>
        </Row>
        <Spacing size={SpacingSizes.XS} />

        <Row>
          <Body style={{ ...styles.text, ...styles.left }}>
            {i18n.t(Translations.WithdrawRoutePendingContributions)}
          </Body>
          <Body style={styles.text}>{pendingContributions}</Body>
        </Row>
        <Spacing size={SpacingSizes.XS} />

        <Row>
          <Body style={{ ...styles.text, ...styles.left }}>
            {i18n.t(Translations.WithdrawRoutePendingWithdrawals)}
          </Body>
          <Body style={styles.text}>{pendingWithdrawals}</Body>
        </Row>
        <Spacing size={SpacingSizes.XS} />

        <Row>
          <Body style={{ ...styles.text, ...styles.left }}>
            {i18n.t(Translations.WithdrawRouteLockedPromoBonus)}
          </Body>
          <Body style={styles.text}>{lockedPromoBonus}</Body>
        </Row>
      </div>
      <Spacing size={SpacingSizes.S} />

      <TextInput
        label={i18n.t(Translations.WithdrawRouteGoalLabel)}
        inputType={TextInputType.Secondary}
        value={goalName}
        readOnly
      />
      <Spacing size={SpacingSizes.M} />

      <SelectionInput
        label={i18n.t(Translations.WithdrawRouteAccountLabel)}
        items={options}
        setSelectedIndex={setSelectedIndex}
        defaultIndex={0}
        readOnly={!isWithdrawalAllowed}
      />
      <Spacing size={SpacingSizes.M} />

      {content[accountType]}

      {isIRAAccount && <AccountNumberSection accountNumber={selectedAccount?.accountNumber} />}

      <Button
        title={primaryCTALabel}
        onClick={handleSubmit}
        loading={isCreatingWithdrawal}
        disabled={disabled}
      />
      <Spacing size={SpacingSizes.M} />
    </MainLayout>
  );
};
